import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

export default function AwardsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Awards · Admin";
  }, []);

  const { list, remove } = useResource("awards", { toastLabel: "award" });
  const { data, isLoading } = list({
    page,
    limit: 20,
    sort: "year:desc",
    ...(search && { search }),
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">About</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Awards</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} award{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/awards/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New award</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search awards by title…"
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/awards/${row._id}`)}
          columns={[
            {
              key: "year",
              label: "Year",
              render: (r) => <span className="font-display text-lg text-accent">{r.year}</span>,
            },
            {
              key: "title",
              label: "Award",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.image} alt={r.title} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.title}</p>
                    <p className="truncate text-xs text-text-muted">
                      {r.awardingBody || "—"}
                    </p>
                  </div>
                </div>
              ),
            },
            {
              key: "description",
              label: "Description",
              render: (r) => (
                <span className="text-text-secondary">
                  {r.description
                    ? r.description.slice(0, 100) + (r.description.length > 100 ? "…" : "")
                    : "—"}
                </span>
              ),
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/awards/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No awards yet",
            description: "Create your first award entry to populate the public Awards page.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title}"?`}
        description="The award will be removed from the public page immediately."
        confirmLabel="Delete award"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
